<template>
  <div class="global-navigation">
    <header class="global-topbar">
      <button
        class="global-brand"
        type="button"
        :aria-label="$route.name === 'ManageOrderDetail' ? $t('manageOrder.back') : 'Dashboard'"
        :class="{ 'global-brand-back': $route.name === 'ManageOrderDetail' }"
        @click="navigate($route.name === 'ManageOrderDetail' ? '/ManageOrder' : '/Dashboard')"
      >
        <v-icon v-if="$route.name === 'ManageOrderDetail'" color="white" size="34">mdi-arrow-left</v-icon>
        <img v-else src="/zaturdayicon.png" alt="" />
      </button>
      <div class="global-heading">
        <h1>{{ pageTitle }}</h1>
        <p>{{ $t("dashboard.updatedAt") }}: {{ currentTime }}</p>
      </div>
      <div class="global-account">
        <button class="global-language" type="button" @click="toggleLanguage">
          {{ $i18n.locale.toUpperCase() }}
        </button>
        <v-menu offset-y>
          <template v-slot:activator="{ on }">
            <button class="global-profile" type="button" v-on="on">
              <div class="global-profile-avatar">
                <img
                  v-if="profileImageSrc"
                  :src="profileImageSrc"
                  alt="Profile image"
                />
                <v-icon v-else color="#00378e" size="34">mdi-account</v-icon>
              </div>
              <span
                ><strong>{{ Username || "User" }}</strong
                ><small>{{ Username || "User" }}@Forward...</small></span
              >
              <img
                class="global-profile-arrow"
                src="@/assets/figma/navbar/arrow-down.svg"
                alt=""
              />
            </button>
          </template>
          <v-list dense>
            <v-list-item @click="openChangePassword">
              <v-list-item-icon>
                <v-icon small>mdi-lock-reset</v-icon>
              </v-list-item-icon>
              <v-list-item-title>{{ $t("message.ChangePassword") }}</v-list-item-title>
            </v-list-item>
            <v-list-item @click="Logout">
              <v-list-item-icon>
                <v-icon small>mdi-logout</v-icon>
              </v-list-item-icon>
              <v-list-item-title>Logout</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </div>
    </header>

    <v-dialog v-model="changePasswordDialog" persistent max-width="560">
      <v-card>
        <v-card-title class="change-password-title">
          {{ $t("message.ChangePassword") }}
          <v-spacer />
          <v-btn
            icon
            :aria-label="$t('message.Close')"
            @click="closeChangePassword"
          >
            <v-icon color="red">mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-text-field
            v-model="oldPassword"
            :label="$t('message.OldPassword')"
            :type="showOldPassword ? 'text' : 'password'"
            :append-icon="showOldPassword ? 'mdi-eye-off' : 'mdi-eye'"
            autocomplete="current-password"
            outlined
            dense
            @click:append="showOldPassword = !showOldPassword"
          />
          <v-text-field
            v-model="newPassword"
            :label="$t('message.NewPassword')"
            :type="showNewPassword ? 'text' : 'password'"
            :append-icon="showNewPassword ? 'mdi-eye-off' : 'mdi-eye'"
            autocomplete="new-password"
            outlined
            dense
            @click:append="showNewPassword = !showNewPassword"
          />
          <v-text-field
            v-model="confirmPassword"
            :label="$t('message.ConfirmNewPassword')"
            :type="showConfirmPassword ? 'text' : 'password'"
            :append-icon="showConfirmPassword ? 'mdi-eye-off' : 'mdi-eye'"
            autocomplete="new-password"
            outlined
            dense
            hide-details
            @click:append="showConfirmPassword = !showConfirmPassword"
          />
        </v-card-text>
        <v-card-actions class="px-6 pb-6">
          <v-spacer />
          <v-btn text @click="closeChangePassword">{{ $t("message.Cancel") }}</v-btn>
          <v-btn
            color="#00378e"
            class="white--text"
            :loading="changingPassword"
            @click="changePassword"
          >
            {{ $t("message.Submit") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <aside
      :class="['global-sidebar', { collapsed: !isSidebarOpen }]"
      aria-label="Main navigation"
    >
      <button
        :class="[
          'global-home',
          {
            active:
              $route.path === '/Dashboard' || $route.path === '/MainMenu',
          },
        ]"
        type="button"
        aria-label="Dashboard"
        @click="navigate('/Dashboard')"
      >
        <img src="@/assets/figma/sidebar/home.svg" alt="" />
      </button>
      <button
        class="global-toggle"
        type="button"
        :aria-expanded="String(isSidebarOpen)"
        aria-label="Toggle main menu"
        @click="toggleSidebar"
      >
        <v-icon color="#00378e" size="24">mdi-menu</v-icon>
      </button>
      <nav>
        <button
          v-for="item in menuItems"
          :key="item.label"
          type="button"
          :class="{ active: isActive(item) }"
          @click="navigate(item.path)"
        >
          <i
            class="global-menu-icon"
            :style="{ '--menu-icon': `url(${item.icon})` }"
            aria-hidden="true"
          ></i
          ><span>{{ item.label }}</span>
        </button>
      </nav>
      <button
        v-if="canManageOrders"
        class="global-order-shortcut"
        :class="{ active: $route.path.startsWith('/ManageOrder') }"
        type="button"
        :aria-label="$t('manageOrder.title')"
        :title="$t('manageOrder.title')"
        @click="navigate('/ManageOrder')"
      >
        <v-icon :color="$route.path.startsWith('/ManageOrder') ? 'white' : '#00378e'" size="26">mdi-cart</v-icon>
        <span v-if="isSidebarOpen">{{ $t('manageOrder.title') }}</span>
      </button>
    </aside>
  </div>
</template>

<script>
import axios from "axios";
import enurl from "@/api/environment";
import Swal from "sweetalert2/dist/sweetalert2.js";

export default {
  name: "GlobalNavigation",
  data() {
    return {
      Username: this.$cookies.get("Username"),
      ProfileImage: localStorage.getItem("ProfileImage") || "",
      url: enurl.apiUrl,
      isSidebarOpen: false,
      changePasswordDialog: false,
      changingPassword: false,
      oldPassword: "",
      newPassword: "",
      confirmPassword: "",
      showOldPassword: false,
      showNewPassword: false,
      showConfirmPassword: false,
      currentTime: new Date().toLocaleTimeString("th-TH", { hour12: false }),
      apiResponseInterceptor: null,
      canManageOrders: false,
      permissionRequest: null,
      menuItems: [
        {
          label: "Transaction",
          icon: require("@/assets/figma/sidebar/transaction.svg"),
          path: "/TransactionRoomBooking",
        },
        {
          label: "Facility",
          icon: require("@/assets/figma/sidebar/facility.svg"),
          path: "/Facility",
        },
        {
          label: "Zone",
          icon: require("@/assets/figma/sidebar/zone.svg"),
          path: "/Zone",
        },
        {
          label: "Utility",
          icon: require("@/assets/figma/sidebar/utility.svg"),
          path: "/Utility",
        },
        {
          label: "Location",
          icon: require("@/assets/figma/sidebar/location.svg"),
          path: "/Location",
        },
        {
          label: "Room Pad",
          icon: require("@/assets/figma/sidebar/roompad.svg"),
          path: "/Roompad",
        },
        {
          label: "Manage User",
          icon: require("@/assets/figma/sidebar/user.svg"),
          path: "/ManageUser",
        },
        {
          label: "Manage Role",
          icon: require("@/assets/figma/sidebar/role.svg"),
          path: "/ManageRole",
        },
      ],
    };
  },
  computed: {
    profileImageSrc() {
      const image = this.ProfileImage.trim();
      if (!image) return "";

      if (image.startsWith("data:image/")) return image;

      let mimeType = "image/jpeg";
      if (image.startsWith("iVBOR")) mimeType = "image/png";
      else if (image.startsWith("UklGR")) mimeType = "image/webp";
      return `data:${mimeType};base64,${image}`;
    },
    pageTitle() {
      const titles = {
        Dashboard: "Workspace Overview",
        MainMenu: "Workspace Overview",
        TransactionRoomBooking: "Transaction",
        Facility: "Facility",
        Zone: "Zone Management",
        Utility: "Utility Management",
        Location: "Location Management",
        Roompad: "Room Pad",
        ManageUser: "Manage User",
        ManageRole: "Manage Role",
        ManageOrder: "Manage Order",
        ManageOrderDetail: "Manage Order",
        ViewSchedule: "View Schedule",
      };
      return (
        titles[this.$route.name] || this.$route.name || "Workspace Overview"
      );
    },
  },
  mounted() {
    this.$emit("navigation-toggle", this.isSidebarOpen);
    this.loadOrderPermission();
    window.addEventListener("profile-image-updated", this.updateProfileImage);
    this.apiResponseInterceptor = axios.interceptors.response.use(
      (response) => {
        this.updateCurrentTime();
        return response;
      },
      (error) => Promise.reject(error)
    );
  },
  beforeDestroy() {
    if (this.permissionRequest) this.permissionRequest.cancel();
    window.removeEventListener("profile-image-updated", this.updateProfileImage);
    if (this.apiResponseInterceptor !== null) {
      axios.interceptors.response.eject(this.apiResponseInterceptor);
    }
  },
  methods: {
    async loadOrderPermission() {
      const request = axios.CancelToken.source();
      this.permissionRequest = request;
      try {
        const response = await axios.get(`${this.url}Permission/getPermisionAppByRoleID`, {
          cancelToken: request.token,
        });
        this.canManageOrders = response.data.status === 0 && (response.data.data || []).some(item =>
          ["manageorder", "ordermanagement"].includes(String(item.appName || "").replace(/\s/g, "").toLowerCase())
        );
      } catch (error) {
        this.canManageOrders = false;
      } finally {
        this.permissionRequest = null;
      }
    },
    updateProfileImage(event) {
      this.ProfileImage =
        event?.detail?.profileImage ??
        localStorage.getItem("ProfileImage") ??
        "";
    },
    updateCurrentTime() {
      this.currentTime = new Date().toLocaleTimeString("th-TH", {
        hour12: false,
      });
    },
    toggleSidebar() {
      this.isSidebarOpen = !this.isSidebarOpen;
      this.$emit("navigation-toggle", this.isSidebarOpen);
    },
    isActive(item) {
      return this.$route.path === item.path;
    },
    navigate(path) {
      if (this.$route.path !== path) this.$router.push(path);
    },
    toggleLanguage() {
      this.$i18n.locale = this.$i18n.locale === "en" ? "th" : "en";
    },
    openChangePassword() {
      this.changePasswordDialog = true;
    },
    closeChangePassword() {
      this.oldPassword = "";
      this.newPassword = "";
      this.confirmPassword = "";
      this.showOldPassword = false;
      this.showNewPassword = false;
      this.showConfirmPassword = false;
      this.changePasswordDialog = false;
    },
    async changePassword() {
      if (!this.oldPassword || !this.newPassword || !this.confirmPassword) {
        await Swal.fire({
          icon: "warning",
          text: this.$t("message.ChangePasswordRequired"),
        });
        return;
      }
      if (this.newPassword !== this.confirmPassword) {
        await Swal.fire({
          icon: "error",
          text: this.$t("message.ChangePasswordMismatch"),
        });
        return;
      }

      this.changingPassword = true;
      try {
        const response = await axios.post(`${this.url}Login/ChangePassword`, {
          currentPassword: this.oldPassword,
          newPassword: this.newPassword,
        });
        if (response.data.status !== 0) {
          throw new Error(
            response.data.message || this.$t("message.ChangePasswordFailed")
          );
        }

        await Swal.fire({
          icon: "success",
          title: this.$t("message.ChangePasswordUpdated"),
          text: this.$t("message.ChangePasswordSuccess"),
          showConfirmButton: false,
          timer: 1500,
        });
        this.closeChangePassword();
        this.Logout();
      } catch (error) {
        await Swal.fire({
          icon: "warning",
          text:
            error?.response?.data?.message ||
            error?.message ||
            this.$t("message.ChangePasswordFailed"),
        });
      } finally {
        this.changingPassword = false;
      }
    },
    Logout() {
      this.$store.dispatch("Logout").finally(() => this.$router.push("/Login"));
    },
  },
};
</script>

<style scoped>
.global-brand.global-brand-back {
  background: #00378e;
}
.global-order-shortcut {
  position: absolute;
  top: calc(var(--sidebar-dashboard-height) + var(--sidebar-dashboard-gap) + 722px + 16px);
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  width: 73px;
  height: 80px;
  border-radius: 50px;
  background: white;
  color: #00378e;
}
.global-sidebar:not(.collapsed) .global-order-shortcut {
  width: 220px;
}
.global-order-shortcut.active .v-icon {
  width: 51px;
  height: 51px;
  border-radius: 50%;
  background: linear-gradient(180deg, #00378e 45%, #ebf4ff);
}
.global-order-shortcut:focus-visible {
  outline: 3px solid #00378e;
  outline-offset: 3px;
}
.global-topbar {
  position: fixed;
  z-index: 40;
  top: 28px;
  right: 38px;
  left: 46px;
  display: flex;
  align-items: center;
  height: 81px;
  padding: 0 9px 0 11px;
  border-radius: 50px;
  background: #fff;
}
.global-navigation {
  --sidebar-dashboard-height: 80px;
  --sidebar-dashboard-gap: 22px;
  zoom: var(--workspace-ui-scale, 1);
}
.global-brand {
  display: grid;
  width: 65px;
  height: 65px;
  place-items: center;
  border: 0;
  border-radius: 50%;
  background: #edf1f5;
}
.global-brand img {
  width: 42px;
  height: 42px;
  object-fit: contain;
}
.global-heading {
  margin-left: 31px;
}
.global-heading h1 {
  margin: 0;
  color: #111;
  font-size: 30px;
  font-weight: 600;
  line-height: 36px;
}
.global-heading p {
  margin: 1px 0 0;
  color: #00378e;
  font-size: 16px;
}
.global-account {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-left: auto;
}
.global-language {
  width: 80px;
  height: 55px;
  border: 0;
  border-radius: 40px;
  background: #00368e;
  box-shadow: 0 4px 4px rgba(0, 0, 0, 0.25);
  color: #fff;
  font-size: 18px;
}
.global-profile {
  display: flex;
  align-items: center;
  width: 214px;
  height: 55px;
  padding: 4px 8px 4px 5px;
  border: 0;
  border-radius: 40px;
  background: #fff;
  box-shadow: 0 4px 4px rgba(0, 0, 0, 0.25);
  text-align: left;
}
.global-profile img {
  width: 49px;
  height: 46px;
}
.global-profile span {
  display: flex;
  min-width: 0;
  flex: 1;
  flex-direction: column;
  margin-left: 10px;
  color: #00378e;
}
.global-profile strong,
.global-profile small {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.global-profile strong {
  font-size: 16px;
  font-weight: 400;
}
.global-profile small {
  font-size: 10px;
}
.global-sidebar {
  position: fixed;
  z-index: 30;
  top: 140px;
  left: 14px;
  width: 220px;
  height: 722px;
  padding: 18px 0;
  border-radius: 48px;
  background: #fff;
  transform: scale(0.8);
  transform-origin: top left;
  transition: width 180ms ease-out;
}
.global-toggle {
  display: grid;
  width: 68px;
  height: 44px;
  margin: 0 0 8px 18px;
  place-items: center;
  border: 0;
  background: transparent;
}
.global-sidebar nav {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.global-sidebar nav button {
  display: flex;
  width: 100%;
  height: 68px;
  align-items: center;
  padding: 0 28px;
  gap: 22px;
  border: 0;
  border-radius: 0 34px 34px 0;
  background: transparent;
  color: #0b0b0b;
  font-size: 18px;
  text-align: left;
  white-space: nowrap;
}
.global-sidebar nav button:hover,
.global-sidebar nav button:focus-visible {
  background: #eef4ff;
  outline: 0;
}
.global-sidebar nav button.active {
  background: linear-gradient(90deg, #dbe8f9 0%, #00378e 47%, #00378e 100%);
  color: #fff;
}
.global-sidebar.collapsed {
  width: 78px;
}
.global-sidebar.collapsed .global-toggle {
  width: 78px;
  margin-left: 0;
}
.global-sidebar.collapsed nav button {
  justify-content: center;
  padding: 0;
  gap: 0;
  border-radius: 34px;
}
.global-sidebar.collapsed nav button span {
  display: none;
}
@media (max-width: 700px) {
  .global-topbar {
    top: 12px;
    right: 12px;
    left: 12px;
  }
  .global-heading {
    margin-left: 12px;
  }
  .global-heading h1 {
    font-size: 20px;
  }
  .global-heading p {
    display: none;
  }
  .global-profile {
    width: 55px;
  }
  .global-profile span,
  .global-profile > .v-icon {
    display: none;
  }
  .global-sidebar {
    display: none;
  }
}
@media (prefers-reduced-motion: reduce) {
  * {
    transition: none !important;
  }
}
.global-sidebar.collapsed {
  top: 152px;
  left: 43px;
  width: 75px;
  height: 856px;
  padding: 0;
  border-radius: 0;
  background: transparent;
}
.global-sidebar.collapsed .global-toggle {
  position: absolute;
  z-index: 1;
  top: calc(
    var(--sidebar-dashboard-height) + var(--sidebar-dashboard-gap)
  );
  left: 0;
  width: 75px;
  height: 80px;
  margin: 0;
  border-radius: 50px 50px 0 0;
  background: transparent;
}
.global-sidebar.collapsed nav {
  position: absolute;
  top: calc(
    var(--sidebar-dashboard-height) + var(--sidebar-dashboard-gap)
  );
  left: 0;
  width: 73px;
  height: 722px;
  align-items: center;
  justify-content: space-evenly;
  gap: 0;
  margin: 0;
  padding-top: 74px;
  border-radius: 50px;
  background: #fff;
}
.global-sidebar.collapsed nav button {
  width: 49px;
  height: 49px;
  flex: none;
  justify-content: center;
  padding: 0;
  gap: 0;
  border-radius: 50%;
}
.global-sidebar.collapsed nav button.active {
  width: 51px;
  height: 49px;
  background: linear-gradient(
    180deg,
    #00378e 0%,
    #00378e 44.7115%,
    #ebf4ff 100%
  );
  box-shadow: none;
}
.global-sidebar:not(.collapsed) {
  top: 152px;
  left: 43px;
  height: 856px;
  padding: 0;
  border-radius: 0;
  background: transparent;
}
.global-sidebar:not(.collapsed) .global-home {
  display: grid;
  width: 73px;
  height: var(--sidebar-dashboard-height);
  place-items: center;
  border-radius: 50px;
  background: #fff;
}
.global-sidebar:not(.collapsed) .global-toggle {
  position: absolute;
  z-index: 1;
  top: calc(
    var(--sidebar-dashboard-height) + var(--sidebar-dashboard-gap)
  );
  left: 0;
  width: 68px;
  height: 74px;
  margin: 0;
}
.global-sidebar:not(.collapsed) nav {
  position: absolute;
  top: calc(
    var(--sidebar-dashboard-height) + var(--sidebar-dashboard-gap)
  );
  left: 0;
  width: 220px;
  height: 722px;
  gap: 8px;
  margin: 0;
  padding-top: 74px;
  border-radius: 48px;
  background: #fff;
}
.global-home {
  position: relative;
  display: none;
  border: 0;
}
.global-sidebar.collapsed .global-home {
  display: grid;
  width: 73px;
  height: var(--sidebar-dashboard-height);
  margin-left: 1px;
  place-items: center;
  border-radius: 50px;
  background: #fff;
}
.global-sidebar .global-home.active::before {
  position: absolute;
  width: 51px;
  height: 49px;
  border-radius: 50%;
  background: linear-gradient(
    180deg,
    #00378e 0%,
    #00378e 44.7115%,
    #ebf4ff 100%
  );
  content: "";
}
.global-menu-icon {
  display: block;
  width: 24px;
  height: 24px;
}
.global-home img {
  position: relative;
  z-index: 1;
  display: block;
  width: 28px;
  height: 28px;
}
.global-home.active img {
  filter: brightness(0) invert(1);
}
.global-menu-icon {
  flex: none;
  background: #00378e;
  -webkit-mask: var(--menu-icon) center / contain no-repeat;
  mask: var(--menu-icon) center / contain no-repeat;
}
.global-sidebar nav button.active .global-menu-icon {
  background: #fff;
}
.global-brand {
  flex: 0 0 65px;
  background: #e0e0e0;
}
.global-brand img {
  width: 24.621px;
  height: 27.576px;
}
.global-heading {
  margin-left: 29px;
}
.global-heading p {
  color: #000;
  line-height: 19px;
}
.global-account {
  margin-right: 10px;
}
.global-profile-avatar {
  display: grid;
  width: 46px;
  min-width: 46px;
  max-width: 46px;
  height: 46px;
  min-height: 46px;
  max-height: 46px;
  flex: 0 0 46px;
  margin-left: 0;
  overflow: hidden;
  place-items: center;
  border-radius: 50%;
  background: #edf1f5;
}
.global-profile-avatar img {
  width: 100%;
  height: 100%;
  border-radius: inherit;
  object-fit: cover;
  object-position: center;
}
.global-profile .global-profile-arrow {
  width: 23.765px;
  height: 23.765px;
  flex: 0 0 23.765px;
}
@media (max-width: 700px) {
  .global-sidebar.collapsed {
    display: none;
  }
  .global-profile .global-profile-arrow {
    display: none;
  }
}
</style>
